import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { WatchQueryFetchPolicy } from '@apollo/client';
import { find, isEqual, keyBy } from 'lodash';

import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';

import {
  DEFAULT_SORTED_VALUE,
  NUMBER_OF_RESULTS_PER_PAGE,
  SEARCH_MAIN_INDEX_ID,
} from 'bundles/search-common/constants';
import { SEARCH_PROVIDER_DEFAULT_CONTEXT_VALUE } from 'bundles/search-common/constants/provider';
import { SearchContext } from 'bundles/search-common/providers/SearchContext';
import type {
  SearchConfig,
  SearchResult,
  SearchResults,
  SearchState,
  SearchStates,
  SearchURLParameters,
  StateUpdates,
} from 'bundles/search-common/providers/searchTypes';
import useSearchParameters from 'bundles/search-common/providers/useSearchParameters';
import useSearchProviderQuery from 'bundles/search-common/providers/useSearchProviderQuery';
import type { SearchSortType } from 'bundles/search-common/types';
import { manipulateFiltersForEventing } from 'bundles/search-common/utils/providerEventingUtils';
import {
  addFiltersToFacetFilters,
  clearFacetFilters,
  combineConfigWithUrlParam,
  removeFiltersFromFacetFilters,
  setFiltersForFacet,
  syncSearchStateToUrl,
} from 'bundles/search-common/utils/providerUtils';

type Props = {
  searchConfigs: SearchConfig[];
  children: ReactNode;
  shouldReplaceRoute?: boolean;
  ssr?: boolean;
  /**
   * Optional query params to be appended to search card links.
   */
  searchCardQueryParams?: Record<string, string | string[] | undefined>;
  shouldDoAutoFiltersLogic?: boolean;
  /**
   * Apollo fetch policy for the underlying search query.
   * Use 'no-cache' for ephemeral queries (e.g. autocomplete) to prevent their
   * results from polluting the shared Apollo cache and overwriting data from the
   * main search query.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
};

function SearchProviderInternal({
  searchConfigs,
  children,
  shouldReplaceRoute = false,
  ssr = true,
  searchCardQueryParams,
  shouldDoAutoFiltersLogic = false,
  fetchPolicy,
}: Props) {
  const logEvent = useRetracked();
  const params = useSearchParameters();
  const searchStatesHashMap = keyBy(combineConfigWithUrlParam(searchConfigs, params), 'id');
  const [searchStates, setSearchStates] = useState<SearchStates>(searchStatesHashMap);
  const prevParams = useRef<SearchURLParameters | undefined>(params);
  const enableAutoAppliedFiltersRef = useRef<Record<string, boolean | undefined>>({});
  const { isLoading, error, results, loadMore } = useSearchProviderQuery(searchStates, searchConfigs, ssr, fetchPolicy);

  // Helper function to get auto-applied filters for a specific search state
  const getAutoAppliedFilters = (id: string): string[][] => {
    const searchResult = results?.find((s) => s.id === id);
    return searchResult?.autoAppliedFilters || [];
  };

  const router = useRouter();
  const track = useTracker();

  const replaceSearchStates = React.useCallback<(a: SearchState[], skipUrlSync?: boolean) => void>(
    (newSearchStates: SearchState[], skipUrlSync = false) => {
      let shouldUpdateSearchState = false;
      let shouldSyncSearchStateToUrl = false;
      const updatedSearchStates = { ...(searchStates || {}) };
      for (const newSearchState of newSearchStates) {
        const searchConfigId = newSearchState.id;
        const existSearchState = find(searchStates, (s: SearchState) => s.id === searchConfigId);
        if (existSearchState) {
          const { syncQueryWithUrl, syncFiltersWithUrl } = existSearchState;
          if (syncQueryWithUrl || syncFiltersWithUrl) shouldSyncSearchStateToUrl = true;
          if (!isEqual(existSearchState, newSearchState)) {
            shouldUpdateSearchState = true;
            updatedSearchStates[searchConfigId] = newSearchState;
          }
        }
      }
      // update searchStates altogether
      if (shouldUpdateSearchState) setSearchStates(updatedSearchStates);

      // Sync URL after state update
      // Skip URL sync when the state change originated from a URL change to prevent duplicate history entries
      if (shouldSyncSearchStateToUrl && !skipUrlSync && newSearchStates[0]) {
        syncSearchStateToUrl(router, newSearchStates[0], shouldReplaceRoute);
      }
    },
    [router, searchStates, shouldReplaceRoute]
  );
  const replaceSearchState = (searchConfigId: string, newSearchState: SearchState) => {
    const existSearchState = find(searchStates, (s: SearchState) => s.id === searchConfigId);
    if (existSearchState) {
      const newSearchStates = { ...(searchStates || {}) };
      const { syncQueryWithUrl, syncFiltersWithUrl } = existSearchState;
      newSearchStates[searchConfigId] = newSearchState;
      setSearchStates(newSearchStates);
      if (syncQueryWithUrl || syncFiltersWithUrl) {
        syncSearchStateToUrl(router, newSearchState, shouldReplaceRoute);
      }
    }
  };

  // Update ref whenever searchStates change
  useEffect(() => {
    Object.keys(searchStates).forEach((id) => {
      enableAutoAppliedFiltersRef.current[id] = searchStates[id]?.enableAutoAppliedFilters;
    });
  }, [searchStates]);

  useEffect(() => {
    if (!isEqual(prevParams.current, params)) {
      const queryChanged = prevParams.current?.query !== params?.query;
      // Use existing enableAutoAppliedFilters values without modification
      const enableAutoAppliedFiltersMap: Record<string, boolean | undefined> = {
        ...enableAutoAppliedFiltersRef.current,
      };
      if (queryChanged) {
        enableAutoAppliedFiltersMap[SEARCH_MAIN_INDEX_ID] = shouldDoAutoFiltersLogic;
      }
      const newSearchStates = combineConfigWithUrlParam(searchConfigs, params, enableAutoAppliedFiltersMap);

      // Always skip URL sync here because this effect responds to URL changes —
      // the URL is the source of truth and already has the correct state.
      // Syncing back would create duplicate history entries, breaking browser back navigation.
      replaceSearchStates(newSearchStates, true);
      prevParams.current = params;
    }
  }, [params, replaceSearchStates, searchConfigs, shouldDoAutoFiltersLogic]);

  const updateQuery = (query: string) => {
    const newSearchStates = Object.keys(searchStates).reduce<SearchState[]>((acc, indexId) => {
      const state = searchStates[indexId];
      if (state) {
        acc.push({
          ...state,
          query,
        });
      }
      return acc;
    }, []);
    replaceSearchStates(newSearchStates);

    logEvent({
      action: 'search',
      trackingName: 'update_query',
      trackingData: { query },
    });
  };

  const updateQueryForIndex = (id: string, query: string) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      query,
      page: undefined,
    };
    replaceSearchState(id, newSearchState);

    logEvent({
      action: 'search',
      trackingName: 'update_query',
      trackingData: { id, query },
    });
  };

  const addFilters = (id: string, filters: string[]) => {
    if (filters.length < 1) return;

    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      page: undefined,
      enableAutoAppliedFilters: false,
    };

    // Add the new filters
    newSearchState.facetFilters = addFiltersToFacetFilters(filters, newSearchState.facetFilters);

    if (currentState.enableAutoAppliedFilters) {
      const autoAppliedFilters = getAutoAppliedFilters(id);
      newSearchState.facetFilters = addFiltersToFacetFilters(autoAppliedFilters.flat(), newSearchState.facetFilters);
    }

    replaceSearchState(id, newSearchState);
    track('perform_search', {
      searchedQuery: newSearchState.query,
      filter: manipulateFiltersForEventing(newSearchState.facetFilters),
    });

    logEvent({
      action: 'search',
      trackingName: 'add_filters',
      trackingData: { id, filters },
    });
  };

  const setFiltersByFacet = (id: string, facet: string, filters: string[]) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      page: undefined,
      enableAutoAppliedFilters: false,
    };
    newSearchState.facetFilters = setFiltersForFacet(facet, filters, newSearchState.facetFilters);

    if (currentState.enableAutoAppliedFilters) {
      const autoAppliedFilters = getAutoAppliedFilters(id);
      newSearchState.facetFilters = addFiltersToFacetFilters(autoAppliedFilters.flat(), newSearchState.facetFilters);
    }

    newSearchState.facetFilters = setFiltersForFacet(facet, filters, newSearchState.facetFilters);
    replaceSearchState(id, newSearchState);

    track('perform_search', {
      searchedQuery: newSearchState.query,
      filter: manipulateFiltersForEventing(newSearchState.facetFilters),
    });

    logEvent({
      action: 'search',
      trackingName: 'apply_filters',
      trackingData: { id, filters },
    });
  };

  const removeFilters = (id: string, filters: string[]) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      page: undefined,
      enableAutoAppliedFilters: false,
    };

    if (newSearchState.facetFilters) {
      // Remove the specified filters
      newSearchState.facetFilters = removeFiltersFromFacetFilters(filters, newSearchState.facetFilters);

      if (currentState.enableAutoAppliedFilters) {
        const autoAppliedFilters = getAutoAppliedFilters(id);
        // Filter out the auto-applied filters that are being removed
        const autoAppliedFiltersToAdd = autoAppliedFilters.flat().filter((autoFilter) => !filters.includes(autoFilter));

        if (autoAppliedFiltersToAdd.length > 0) {
          newSearchState.facetFilters = addFiltersToFacetFilters(autoAppliedFiltersToAdd, newSearchState.facetFilters);
        }
      }

      replaceSearchState(id, newSearchState);
    }

    track('perform_search', {
      searchedQuery: newSearchState.query,
      filter: manipulateFiltersForEventing(newSearchState.facetFilters),
    });

    logEvent({
      action: 'search',
      trackingName: 'remove_filters',
      trackingData: { id, filters },
    });
  };

  const clearFilters = (id: string) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      facetFilters: clearFacetFilters(currentState.facetFilters),
      page: undefined,
      enableAutoAppliedFilters: false,
    };

    replaceSearchState(id, newSearchState);

    track('perform_search', {
      searchedQuery: newSearchState.query,
      filter: manipulateFiltersForEventing(newSearchState.facetFilters),
    });

    logEvent({
      action: 'search',
      trackingName: 'clear_filters',
      trackingData: { id },
    });
  };

  const setPage = (id: string, page: number) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      page,
      enableAutoAppliedFilters: false,
    };

    if (currentState.enableAutoAppliedFilters) {
      const autoAppliedFilters = getAutoAppliedFilters(id);
      newSearchState.facetFilters = addFiltersToFacetFilters(autoAppliedFilters.flat(), newSearchState.facetFilters);
    }

    replaceSearchState(id, newSearchState);

    logEvent({
      action: 'search',
      trackingName: 'set_page',
      trackingData: { id, page },
    });
  };

  const loadNextPage = () => {
    const id = Object.keys(searchStates)[0]; // id of the main config
    const inferredNextPage = Math.floor(
      (results?.find((s) => s.id === id)?.elements?.length || NUMBER_OF_RESULTS_PER_PAGE) / NUMBER_OF_RESULTS_PER_PAGE
    );

    if (loadMore) {
      loadMore(inferredNextPage);
      logEvent({
        action: 'search',
        trackingName: 'set_page',
        trackingData: { id, page: inferredNextPage },
      });
    }
  };

  const setSortBy = (id: string, sortBy: SearchSortType) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = {
      ...currentState,
      sortBy,
      page: undefined,
      enableAutoAppliedFilters: false,
    };

    if (currentState.enableAutoAppliedFilters) {
      const autoAppliedFilters = getAutoAppliedFilters(id);
      newSearchState.facetFilters = addFiltersToFacetFilters(autoAppliedFilters.flat(), newSearchState.facetFilters);
    }
    replaceSearchState(id, newSearchState);
    logEvent({
      action: 'search',
      trackingName: 'set_sort_by',
      trackingData: { sortBy },
    });
    track('perform_search', {
      searchedQuery: newSearchState.query,
      filter: [`sortBy:${sortBy}`, ...(manipulateFiltersForEventing(newSearchState.facetFilters) || [])],
    });
  };

  const setExplain = (id: string, explain: boolean) => {
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = { ...currentState, explain };
    replaceSearchState(id, newSearchState);
    logEvent({
      action: 'search',
      trackingName: 'set_explain',
      trackingData: { explain },
    });
  };

  const applySearchStateUpdates = (id: string, searchStateUpdates: StateUpdates) => {
    const updates = Object.keys(searchStateUpdates);
    const currentState = searchStates[id];
    if (!currentState) return;

    const newSearchState: SearchState = { ...currentState };

    if (updates.includes('query')) {
      newSearchState.query = searchStateUpdates.query;
      logEvent({
        action: 'search',
        trackingName: 'update_query',
        trackingData: { query: searchStateUpdates.query },
      });
    }

    if (updates.includes('clearFilters') && searchStateUpdates.clearFilters) {
      newSearchState.facetFilters = clearFacetFilters(newSearchState?.facetFilters);
      logEvent({
        action: 'search',
        trackingName: 'clear_filters',
        trackingData: { id },
      });
    }

    if (updates.includes('addFilters') && searchStateUpdates.addFilters) {
      newSearchState.facetFilters = addFiltersToFacetFilters(
        searchStateUpdates.addFilters,
        newSearchState.facetFilters
      );
      logEvent({
        action: 'search',
        trackingName: 'add_filters',
        trackingData: { id, filters: searchStateUpdates.addFilters },
      });
    }

    if (updates.includes('removeFilters') && searchStateUpdates.removeFilters) {
      newSearchState.facetFilters = removeFiltersFromFacetFilters(
        searchStateUpdates.removeFilters,
        newSearchState.facetFilters || []
      );
      logEvent({
        action: 'search',
        trackingName: 'remove_filters',
        trackingData: { id, filters: searchStateUpdates.removeFilters },
      });
    }

    if (updates.includes('setFiltersByFacet') && searchStateUpdates.setFiltersByFacet) {
      Object.keys(searchStateUpdates.setFiltersByFacet).forEach((facet) => {
        const filters = searchStateUpdates.setFiltersByFacet?.[facet];
        if (filters) newSearchState.facetFilters = setFiltersForFacet(facet, filters, newSearchState.facetFilters);
        logEvent({
          action: 'search',
          trackingName: 'apply_filters',
          trackingData: { id, filters },
        });
      });
    }

    if (updates.includes('sortBy')) {
      newSearchState.sortBy = searchStateUpdates.sortBy;
      logEvent({
        action: 'search',
        trackingName: 'set_sort_by',
        trackingData: { sortBy: searchStateUpdates.sortBy },
      });
    }

    if (updates.includes('page')) {
      newSearchState.page = searchStateUpdates.page;
      logEvent({
        action: 'search',
        trackingName: 'set_page',
        trackingData: { id, page: searchStateUpdates.page },
      });
    }

    if (updates.includes('enableAutoAppliedFilters')) {
      newSearchState.enableAutoAppliedFilters = searchStateUpdates.enableAutoAppliedFilters;
    }
    if (updates.includes('clearAutoAppliedFilters')) {
      newSearchState.enableAutoAppliedFilters = false;
    }

    replaceSearchState(id, newSearchState);

    track('perform_search', {
      searchedQuery: newSearchState.query,
      filter: manipulateFiltersForEventing(newSearchState.facetFilters),
    });

    if (
      updates.includes('clearFilters') &&
      updates.includes('sortBy') &&
      searchStateUpdates.sortBy === DEFAULT_SORTED_VALUE
    ) {
      logEvent({
        action: 'reset',
        trackingName: 'clear_filters_and_sort_by',
        trackingData: { id },
      });
    }
  };

  function getSearchResults(): SearchResults | undefined;
  function getSearchResults(id: string): SearchResult | undefined;
  function getSearchResults(id?: string) {
    if (id) {
      return results?.find((s) => s.id === id);
    } else {
      return results;
    }
  }

  return (
    <SearchContext.Provider
      value={{
        getSearchResults,
        isLoading,
        error,
        updateQuery,
        updateQueryForIndex,
        addFilters,
        removeFilters,
        clearFilters,
        setPage,
        setFiltersByFacet,
        setSortBy,
        setExplain,
        applySearchStateUpdates,
        loadNextPage,
        searchCardQueryParams,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

function SearchProvider({ children, ...props }: Props) {
  // initiate provider with default value so other hooks (e.g. useSearchProviderQuery) can check if provider is defined
  return (
    <SearchContext.Provider value={SEARCH_PROVIDER_DEFAULT_CONTEXT_VALUE}>
      <SearchProviderInternal {...props}>{children}</SearchProviderInternal>
    </SearchContext.Provider>
  );
}

export default SearchProvider;
